import { useMemo, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Modal from "@/components/base/Modal";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { Input, Select } from "@/components/base/Field";
import { ErrorState, LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAsync } from "@/hooks/useAsync";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { addClass, deleteClass, getClasses, getSubjects, updateClass } from "@/services/academicService";
import { getActiveSchoolYear, getCycles } from "@/services/schoolService";
import { getEnrollments } from "@/services/studentService";
import type { Cycle, CycleCode, SchoolClass, Subject } from "@/types";
import { useUrlSearch } from "@/hooks/useUrlSearch";
import { isInClass } from "@/utils/enrollment";

/* ------------------------------------------------------------------------- */
/* Composants locaux                                                          */
/* ------------------------------------------------------------------------- */

interface ClassRowData {
  cycle: Cycle;
  rows: SchoolClass[];
}

interface ClassTableProps {
  /** Filtres actifs : retour à la page 1 quand ils changent. */
  filterKey: string;
  data: ClassRowData;
  subjectNames: Record<string, string>;
  headcounts: Record<string, number>;
  onEdit: (cls: SchoolClass) => void;
  onDelete: (cls: SchoolClass) => void;
}

function ClassTable({ data, filterKey, subjectNames, headcounts, onEdit, onDelete }: ClassTableProps) {
  const page = usePager().slice(data.rows, filterKey);
  const isPrimary = data.cycle.code === "PRIMAIRE";
  const accentTone = isPrimary ? "warning" : "secondary";
  const dot = isPrimary ? "bg-warning-500" : "bg-secondary-500";
  const chip = isPrimary ? "bg-warning-100 text-warning-800" : "bg-secondary-100 text-secondary-800";

  return (
    <section className="mb-8">
      <div className="mb-3 flex items-center gap-3">
        <span className={`h-2.5 w-2.5 rounded-full ${dot}`}></span>
        <h4 className="text-sm font-semibold font-heading text-foreground-900">
          Cycle {data.cycle.label}
        </h4>
        <Badge tone={accentTone}>{data.rows.length} classes</Badge>
      </div>

      <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] border-collapse">
            <thead>
              <tr className="border-b border-background-200 bg-background-50/60 text-left text-xs uppercase tracking-wider text-foreground-500">
                <th className="px-5 py-3 font-medium">Classe</th>
                <th className="px-5 py-3 font-medium">Effectif</th>
                <th className="px-5 py-3 font-medium">Matières</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {page.items.map((cls) => (
                <tr
                  key={cls.id}
                  className="border-b border-background-200 transition-colors last:border-0 hover:bg-background-100/60"
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      {cls.shortName && (
                        <span
                          className={`inline-flex h-7 min-w-7 shrink-0 items-center justify-center rounded-md px-2 text-xs font-semibold ${chip}`}
                        >
                          {cls.shortName}
                        </span>
                      )}
                      <span className="text-sm font-medium text-foreground-900">{cls.name}</span>
                      {cls.level !== undefined && (
                        <span className="text-xs text-foreground-400" title="Niveau dans le parcours : pas de réinscription dans une classe de niveau inférieur">
                          niveau {cls.level}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-foreground-700">
                    <span className="font-semibold text-foreground-900">
                      {headcounts[cls.id] ?? 0}
                    </span>{" "}
                    élèves
                  </td>
                  <td
                    className="px-5 py-3.5 text-sm text-foreground-700"
                    title={cls.subjectIds
                      .map((id) => subjectNames[id])
                      .filter(Boolean)
                      .join(", ")}
                  >
                    <span className="font-semibold text-foreground-900">
                      {cls.subjectIds.length}
                    </span>{" "}
                    matières
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => onEdit(cls)}
                        aria-label={`Modifier ${cls.name}`}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                      >
                        <i className="ri-pencil-line"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(cls)}
                        aria-label={`Supprimer ${cls.name}`}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-accent-50 hover:text-accent-700 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination pager={page} className="border-t border-background-100" />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------------- */
/* Page                                                                       */
/* ------------------------------------------------------------------------- */

interface ClassFormState {
  cycleId: string;
  name: string;
  shortName: string;
  /** Vide : déduit du nom par le serveur (CP1, 7ème, Terminale...). */
  level: string;
  subjectIds: string[];
}

async function loadClassesPage() {
  const schoolYear = getActiveSchoolYear();
  const [cycles, classes, subjects, enrollments] = await Promise.all([
    getCycles(),
    getClasses(),
    getSubjects(),
    getEnrollments(),
  ]);
  const current = enrollments.filter((e) => e.schoolYear === schoolYear && isInClass(e.status));
  return { cycles, classes, subjects, enrollments: current };
}

export default function ClassesPage() {
  const { notify } = useToast();
  const { data, loading, error, reload } = useAsync(loadClassesPage, []);

  const [search, setSearch] = useUrlSearch();
  const [cycleFilter, setCycleFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SchoolClass | null>(null);
  const [form, setForm] = useState<ClassFormState>({
    cycleId: "",
    name: "",
    shortName: "",
    level: "",
    subjectIds: [],
  });
  const [toDelete, setToDelete] = useState<SchoolClass | null>(null);

  const cycles = useMemo(() => data?.cycles ?? [], [data]);
  const classes = data?.classes ?? [];
  const subjects = useMemo(() => data?.subjects ?? [], [data]);
  const enrollments = useMemo(() => data?.enrollments ?? [], [data]);

  const cycleLabels = useMemo(
    () => Object.fromEntries(cycles.map((cycle) => [cycle.id, cycle.label])) as Record<string, string>,
    [cycles],
  );
  const subjectNames = useMemo(
    () => Object.fromEntries(subjects.map((subject) => [subject.id, subject.name])) as Record<string, string>,
    [subjects],
  );
  const subjectsByCycle = useMemo(() => {
    const map: Record<string, Subject[]> = {};
    subjects.forEach((subject) => {
      if (!map[subject.cycleId]) map[subject.cycleId] = [];
      map[subject.cycleId].push(subject);
    });
    return map;
  }, [subjects]);
  const headcounts = useMemo(() => {
    const counts: Record<string, number> = {};
    enrollments.forEach((enrollment) => {
      counts[enrollment.classId] = (counts[enrollment.classId] ?? 0) + 1;
    });
    return counts;
  }, [enrollments]);

  if (loading && !data) return <LoadingState label="Chargement des classes..." />;
  if (error || !data) {
    return <ErrorState message={error ?? "Configuration introuvable."} onRetry={reload} />;
  }

  const countByCycleCode = (code: CycleCode) =>
    classes.filter((cls) => cycles.find((cycle) => cycle.id === cls.cycleId)?.code === code).length;
  const primaryCount = countByCycleCode("PRIMAIRE");
  const secondaryCount = countByCycleCode("SECONDAIRE");

  const term = search.trim().toLowerCase();
  const visibleCycles = cycles.filter((cycle) => cycleFilter === "all" || cycle.id === cycleFilter);
  const filteredRows = visibleCycles.map<ClassRowData>((cycle) => ({
    cycle,
    rows: classes.filter((cls) => {
      if (cls.cycleId !== cycle.id) return false;
      if (!term) return true;
      return cls.name.toLowerCase().includes(term) || cls.shortName.toLowerCase().includes(term);
    })
      // Ordre du parcours (niveau), puis par nom.
      .sort((a, b) => (a.level ?? 99) - (b.level ?? 99) || a.name.localeCompare(b.name)),
  }));
  const tablesToRender = filteredRows.filter((item) => item.rows.length > 0);
  const totalVisible = filteredRows.reduce((sum, item) => sum + item.rows.length, 0);

  const openCreate = () => {
    const defaultCycle = cycleFilter !== "all" ? cycleFilter : cycles[0]?.id ?? "";
    setEditing(null);
    setForm({ cycleId: defaultCycle, name: "", shortName: "", level: "", subjectIds: [] });
    setOpen(true);
  };

  const openEdit = (cls: SchoolClass) => {
    setEditing(cls);
    setForm({
      cycleId: cls.cycleId,
      name: cls.name,
      shortName: cls.shortName ?? "",
      level: cls.level !== undefined ? String(cls.level) : "",
      subjectIds: [...cls.subjectIds],
    });
    setOpen(true);
  };

  const handleCycleChange = (cycleId: string) => {
    setForm((prev) => ({ ...prev, cycleId, subjectIds: [] }));
  };

  const toggleSubject = (subjectId: string) => {
    setForm((prev) => ({
      ...prev,
      subjectIds: prev.subjectIds.includes(subjectId)
        ? prev.subjectIds.filter((id) => id !== subjectId)
        : [...prev.subjectIds, subjectId],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      notify("Le nom de la classe est obligatoire.", "error");
      return;
    }
    if (!form.shortName.trim()) {
      notify("Le sigle de la classe est obligatoire.", "error");
      return;
    }

    const payload = {
      cycleId: form.cycleId,
      name: form.name.trim(),
      shortName: form.shortName.trim(),
      level: form.level ? Number(form.level) : undefined,
      subjectIds: form.subjectIds,
    };

    if (editing) {
      await updateClass(editing.id, payload);
      notify("Classe mise à jour.");
    } else {
      await addClass(payload);
      notify(`Classe « ${payload.name} » ajoutée.`);
    }
    await reload();
    setOpen(false);
  };

  const availableSubjects = subjectsByCycle[form.cycleId] ?? [];

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Classes"
        subtitle={`${classes.length} classes — ${primaryCount} primaire · ${secondaryCount} secondaire`}
        icon="ri-building-4-line"
        actions={
          <Button icon="ri-add-line" onClick={openCreate}>
            Ajouter une classe
          </Button>
        }
      />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher une classe (nom ou sigle)..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="relative sm:w-56">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-filter-line text-sm"></i>
          </span>
          <select
            value={cycleFilter}
            onChange={(event) => setCycleFilter(event.target.value)}
            className="w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-9 text-sm text-foreground-800 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          >
            <option value="all">Tous les cycles</option>
            {cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.label}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-arrow-down-s-line text-base"></i>
          </span>
        </div>
      </div>

      {totalVisible === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-background-300 bg-background-100/50 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-building-4-line text-2xl"></i>
          </span>
          <p className="text-sm text-foreground-600">
            Aucune classe ne correspond à votre recherche.
          </p>
        </div>
      ) : (
        tablesToRender.map((item) => (
          <ClassTable
            key={item.cycle.id}
            data={item}
            filterKey={`${search}|${cycleFilter}`}
            subjectNames={subjectNames}
            headcounts={headcounts}
            onEdit={openEdit}
            onDelete={setToDelete}
          />
        ))
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Modifier une classe" : "Ajouter une classe"}
        subtitle={
          editing ? `Cycle ${cycleLabels[editing.cycleId] ?? "—"}` : "Rattachez la classe à un cycle"
        }
        icon="ri-building-4-line"
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button icon="ri-save-3-line" onClick={handleSave}>
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <Select
            label="Cycle"
            required
            hint={cycles.length === 0 ? "Aucun cycle : ouvrez-en un dans Paramètres › Cycles." : undefined}
            value={form.cycleId}
            onChange={(event) => handleCycleChange(event.target.value)}
          >
            {cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.label}
              </option>
            ))}
          </Select>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Nom de la classe"
              required
              placeholder="ex. CE1"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            />
            <Input
              label="Sigle"
              required
              placeholder="ex. CE1"
              value={form.shortName}
              onChange={(event) => setForm((prev) => ({ ...prev, shortName: event.target.value }))}
            />
            <Input
              label="Niveau"
              type="number"
              min={1}
              max={30}
              placeholder="Déduit du nom si vide"
              hint="Petite Section = 1 … CM2 = 9, 7ème = 10 … Terminale = 16. Un élève n'est jamais réinscrit dans une classe de niveau inférieur."
              wrapperClass="sm:col-span-2"
              value={form.level}
              onChange={(event) => setForm((prev) => ({ ...prev, level: event.target.value }))}
            />
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-foreground-700">
              Matières du {cycleLabels[form.cycleId] ?? "cycle"}
            </p>
            {availableSubjects.length === 0 ? (
              <p className="text-xs text-foreground-400">
                Aucune matière disponible pour ce cycle.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableSubjects.map((subject) => {
                  const selected = form.subjectIds.includes(subject.id);
                  return (
                    <button
                      key={subject.id}
                      type="button"
                      onClick={() => toggleSubject(subject.id)}
                      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                        selected
                          ? "border-primary-500 bg-primary-600 text-white"
                          : "border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                      }`}
                    >
                      {selected && (
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-check-line text-xs leading-none"></i>
                        </span>
                      )}
                      {subject.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={toDelete !== null}
        title="Supprimer la classe"
        message={`Voulez-vous vraiment supprimer la classe « ${toDelete?.name ?? ""} » ? Cette action est définitive.`}
        confirmLabel="Oui, supprimer"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          await deleteClass(toDelete.id);
          await reload();
          notify("Classe supprimée.", "info");
        }}
      />
    </div>
  );
}